import type { AttachedState } from './api'

/**
 * Gather the AttachedState the widget POSTs alongside the user's words.
 *
 * v1 captures the route + parsed route params. Future expansion can read
 * zustand stores (active patient, consultation, recording) to enrich the
 * map without changing the contract — keys are open-ended.
 */
export function collectAttachedState(): AttachedState {
  const path = typeof window !== 'undefined' ? window.location.pathname : '/'
  const routeParams = parseRouteParams(path)

  return {
    route: path,
    route_params: routeParams,
  }
}

/**
 * Best-effort param extraction from the current URL path. The frontend
 * doesn't have a single registry of route shapes, so we identify a few
 * common ones by prefix and pull the id-shaped segment after them. Falls
 * back to an empty map if nothing recognised.
 */
function parseRouteParams(path: string): Record<string, string | undefined> {
  const segments = path.split('/').filter(Boolean)
  const params: Record<string, string | undefined> = {}

  for (let i = 0; i < segments.length - 1; i++) {
    const segment = segments[i]
    const next = segments[i + 1]
    if (!isLikelyId(next)) continue

    switch (segment) {
      case 'patients':
        params.patient_uuid = next
        break
      case 'notes':
        params.note_id = next
        break
      case 'appointments':
        params.appointment_uuid = next
        break
      case 'encounters':
        params.encounter_uuid = next
        break
      case 'consultations':
        params.consultation_uuid = next
        break
      default:
        // Generic capture for future routes — surface the segment + value
        // so triage can see what the user was looking at without us
        // having to enumerate every known route shape.
        params[`${segment}_id`] = next
    }
  }

  return params
}

const ID_LIKE = /^([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}|[0-9]+|[a-z0-9_]{8,})$/i

function isLikelyId(s: string | undefined): boolean {
  return !!s && ID_LIKE.test(s)
}
